import { useLocation } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import ButtonIcon from '../ui/ButtonIcon';
import { useCurrentUser } from '../../services/userHooks';
import { useUpdateFolderSort } from '../../services/folders';
import { useUpdateTagSort } from '../../services/tagService';
import { useNavigationMode } from './hooks/useNavigationMode';
import { useFolderBySlug } from '../../hooks/useFolderBySlug';
import { useMobileNavSidebar, useSidebarNav } from '../../hooks/useUserPreferences';
import type { SidebarFolderSortMode, SidebarTagSortMode } from '../../types/api';
import SortableFolderList from './SortableFolderList';
import Section from './Section';
import TagItem from './TagItem';
import MobileNavLinks from './MobileNavLinks';
import DesktopFooterNavLinks from './DesktopFooterNavLinks';
import SettingsNavigation from './SettingsNavigation';
import SectionSortMenu, { type SectionSortOption } from './SectionSortMenu';
import AddFolderModal from '../AddFolderModal';
import AddTagModal from '../AddTagModal';
import SidebarNavShell from './SidebarNavShell';
import { SidebarItemModalsProvider } from './SidebarItemModalsContext';
import SidebarPrimaryNav from './SidebarPrimaryNav';
import DocsNavigation from './DocsNavigation';
import { activeGroupFromPath } from '../Docs/docGroupRoutes';
import LoadingScreen from '../LoadingScreen';

const FOLDER_SORT_OPTIONS: SectionSortOption<SidebarFolderSortMode>[] = [
  { value: 'manual', label: <Trans>Manual</Trans> },
  { value: 'name', label: <Trans>Name</Trans> },
  { value: 'oldest', label: <Trans>Oldest</Trans> },
  { value: 'newest', label: <Trans>Newest</Trans> },
  { value: 'count', label: <Trans>Count</Trans> },
  { value: 'recently_used', label: <Trans>Recently used</Trans> },
];

const TAG_SORT_OPTIONS: SectionSortOption<SidebarTagSortMode>[] = [
  { value: 'name', label: <Trans>Name</Trans> },
  { value: 'oldest', label: <Trans>Oldest</Trans> },
  { value: 'newest', label: <Trans>Newest</Trans> },
  { value: 'count', label: <Trans>Count</Trans> },
  { value: 'recently_used', label: <Trans>Recently used</Trans> },
];

function SidebarNavigation() {
  // Subscribe to locale changes to re-render when language changes
  useLingui();
  const { currentUser } = useCurrentUser();
  const location = useLocation();
  const { mode, currentFolderSlug, currentTagSlug } = useNavigationMode();
  const currentFolder = useFolderBySlug(currentFolderSlug || '');
  const currentFolderId = currentFolder?.id;
  const { closeMobileNavSidebar } = useMobileNavSidebar();
  const { sidebarNavPreferences, setSidebarNavSectionOpen } = useSidebarNav();
  const updateFolderSort = useUpdateFolderSort();
  const updateTagSort = useUpdateTagSort();
  const [isAddFolderModalOpen, setIsAddFolderModalOpen] = useState(false);
  const [isAddTagModalOpen, setIsAddTagModalOpen] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);

  // On hard refresh / direct navigation only, scroll the active sidebar item
  // into view. If the active item lives inside a collapsed Tags or Folders
  // section, respect the user's preference and skip the scroll rather than
  // auto-expanding the section on their behalf.
  // Empty deps: runs once on mount, so client-side navigation is not affected.
  useEffect(() => {
    if (mode === 'folder' && sidebarNavPreferences.sections.folders === false) {
      return;
    }
    if (mode === 'tags' && sidebarNavPreferences.sections.tags === false) {
      return;
    }

    // Defer one frame so the layout is settled before we query and scroll.
    const rafId = requestAnimationFrame(() => {
      const activeEl =
        scrollerRef.current?.querySelector<HTMLElement>('[data-active="true"]');
      // 'center' keeps the active item clear of the sticky logo at the top.
      // For items near the bottom of the list, the browser clamps to the
      // maximum scroll position; the scroller's bottom padding then keeps the
      // item above the fixed profile overlay.
      activeEl?.scrollIntoView({ block: 'center', behavior: 'instant' });
    });

    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if we're on a settings or docs-center page. The docs center spans
  // several top-level groups (Documentation, Developers, About & Legal), each
  // sharing the same group-aware sidebar.
  const isSettingsPage = location.pathname.startsWith('/settings');
  const isDocsPage = activeGroupFromPath(location.pathname) !== null;

  const userTags = currentUser?.tags || [];
  const folderSortMode = currentUser?.folderSort ?? 'manual';
  const tagSortMode = currentUser?.tagSort ?? 'name';

  const handleFolderSortChange = (sortMode: SidebarFolderSortMode) => {
    if (!currentUser?.formKey) {
      return;
    }
    updateFolderSort.mutate({
      sortMode,
      formKey: currentUser.formKey,
    });
  };

  const handleTagSortChange = (sortMode: SidebarTagSortMode) => {
    if (!currentUser?.formKey) {
      return;
    }
    updateTagSort.mutate({
      sortMode,
      formKey: currentUser.formKey,
    });
  };

  const sortedFolders = currentUser?.folders || [];

  // Render Settings Navigation
  if (isSettingsPage) {
    return <SettingsNavigation />;
  }

  // Render Docs Navigation
  if (isDocsPage) {
    return <DocsNavigation />;
  }

  if (!currentUser) {
    return <LoadingScreen />;
  }

  // Render Main Navigation
  return (
    <SidebarItemModalsProvider>
      <SidebarNavShell
        scrollerRef={scrollerRef}
        scrollerHeader={
          <div className="side-nav-mobile-close">
            <ButtonIcon
              icon="close"
              onClick={closeMobileNavSidebar}
              title={t`Close`}
              className="mt-2 p-1"
            />
          </div>
        }
        footer={<DesktopFooterNavLinks />}
        navExtras={
          <>
            <img
              id="drag_image_article_one"
              className="drag_image hidden"
              src="/static/img/drag_proxy_one.png"
              alt=""
            />
            <img
              id="drag_image_article_two"
              className="drag_image hidden"
              src="/static/img/drag_proxy_two.png"
              alt=""
            />
            <img
              id="drag_image_article_three"
              className="drag_image hidden"
              src="/static/img/drag_proxy_three.png"
              alt=""
            />
            <div
              id="scroll_indicator_side_wrapper"
              className="iScrollVerticalScrollbar scroll_indicator_wrapper"
            >
              <div
                id="scroll_indicator"
                className="iScrollIndicator scroll_indicator"
              ></div>
            </div>
          </>
        }
      >
        <SidebarPrimaryNav mode={mode} counts={currentUser?.counts} />

        <Section
          title={t`Tags`}
          collapsible={userTags.length > 0}
          isOpen={sidebarNavPreferences.sections.tags}
          onToggle={(isOpen) => {
            if (userTags.length == 0) {
              setIsAddTagModalOpen(true);
            } else {
              setSidebarNavSectionOpen('tags', isOpen);
            }
          }}
          headerActions={
            <SectionSortMenu
              sortMode={tagSortMode}
              sortOptions={TAG_SORT_OPTIONS}
              showSortOptions={userTags.length > 1}
              onSortChange={handleTagSortChange}
              onAdd={() => setIsAddTagModalOpen(true)}
              addLabel={<Trans>Add tag</Trans>}
              addTitle={t`Add tag`}
              optionsTitle={t`Tag options`}
            />
          }
        >
          <ul id="tags" className="list-reset" aria-label={t`Tags`}>
            {userTags.map((tag) => (
              <TagItem
                key={tag.id}
                tag={tag}
                isActive={currentTagSlug === tag.slug}
                dropDisabled={!sidebarNavPreferences.sections.tags}
              />
            ))}
          </ul>
        </Section>

        <Section
          title={t`Folders`}
          collapsible={sortedFolders.length > 0}
          isOpen={sidebarNavPreferences.sections.folders}
          onToggle={(isOpen) => {
            if (sortedFolders.length == 0) {
              setIsAddFolderModalOpen(true);
            } else {
              setSidebarNavSectionOpen('folders', isOpen);
            }
          }}
          headerActions={
            <SectionSortMenu
              sortMode={folderSortMode}
              sortOptions={FOLDER_SORT_OPTIONS}
              showSortOptions={sortedFolders.length > 1}
              onSortChange={handleFolderSortChange}
              onAdd={() => setIsAddFolderModalOpen(true)}
              addLabel={<Trans>Add folder</Trans>}
              addTitle={t`Add folder`}
              optionsTitle={t`Folder options`}
            />
          }
        >
          {sortedFolders.length > 0 && (
            <SortableFolderList
              folders={sortedFolders}
              currentFolderId={currentFolderId}
              sortingEnabled={folderSortMode === 'manual'}
              dropDisabled={!sidebarNavPreferences.sections.folders}
            />
          )}
        </Section>

        <MobileNavLinks />
      </SidebarNavShell>

      <AddFolderModal
        isOpen={isAddFolderModalOpen}
        onClose={() => setIsAddFolderModalOpen(false)}
      />

      <AddTagModal
        isOpen={isAddTagModalOpen}
        onClose={() => setIsAddTagModalOpen(false)}
      />
    </SidebarItemModalsProvider>
  );
}

export default SidebarNavigation;
